import type {ReactElement, TransitionEvent} from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isFacebookAccount, isInstagramAccount} from '@Components/social-media/account.vo';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import {MultiPostDesignActionPurposes, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {
  DesignSelection,
  PublishableUrlUpdatePayload,
  SocialPostPlatformSelectionTransitionEventDetail,
} from '@Components/social-media/social-media.types';
import {
  MultiGraphicDesignSelectorModes,
  SocialPostCaptionOverwriteEvent,
  SocialPostPlatformSelectionTransitionEvent,
} from '@Components/social-media/social-media.types';
import {
  useAccountIdsToPublishTo,
  useAccountResizeCompletedStatusForPosters,
  usePostPublishingParamsForAccount,
  usePublishingParams,
  usePublishingStatusForAccountWithMediaType,
  useReelPublishingParamsForAccount,
  useSocialPostPublishingAction,
  useStoryGuidelineForAccount,
  useStoryPublishingParamsForAccount,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {
  SocialMediaWizardContentSize,
  SocialMediaWizardMediaTypeRadioButtonIds,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  useDesignsGraphicsMetaMapForAccountWithMediaType,
  useDesignsToPublish,
  useDoesAnyPosterRequireResizeForAccount,
  useHasAnyPosterResizeErroredForAccount,
  useIsAnyPosterLoadingForAccount,
  useIsAnyPosterResizingForAccount,
  useIsAnyPosterResizingForAccountWithMediaType,
  useIsPublishingVideoForAccount,
  useWizardDesignsGraphicMetaMap,
  useWizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {
  removeAccountFromPublishingParams,
  removeAccountIdFromPublishing,
  setMediaTypeSelectionToPost,
  setMediaTypeSelectionToPostAndStory,
  setMediaTypeSelectionToReel,
  setMediaTypeSelectionToReelAndStory,
  setMediaTypeSelectionToStory,
  updateDesignToResize,
  updateMultiGraphicDesignSelectorMode,
  updatePublishableUrl,
  updatePublishingParams,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {usePublishParamsCoverFlow, usePublishParamsResizePipeline} from './social-media-post-publish-params.hooks';
import {PublishParamsPreviewCta} from './components/publish-params-preview-cta/publish-params-preview-cta';
import {PublishingPost} from '@Components/social-media-wizard/components/publishing-post';
import {doHideRemoveAccountConfirmationForUser, getHideSMRemoveAccountConfirmationPreference} from '@Libraries/social-media-wizard-accounts-library';
import {LoadingStates} from '@Utils/loading.util';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import type {SocialPostsDesignActionModalProps} from '@Components/modals/social-post-design-action-modal';
import {openDesignActionModal, PANEL_ID as SocialPostSelectDesignPanelID} from '@Components/modals/social-post-design-action-modal';
import {PlatformPublishFields} from '@Components/social-media-wizard/components/platform-publish-fields/platform-publish-fields';
import type {RadioButtonOption} from '@Components/radio-button/radio-button';
import {RadioButton} from '@Components/radio-button/radio-button';
import {SocialMediaPublishParamsStoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import styles from './social-media-post-publish-params.module.scss';
import {useAppDispatch} from '@/hooks';
import {noop} from '@/utils/general.util';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';

interface SocialMediaPostPublishParamsProps {
  account: SocialAccount;
  size?: SocialMediaWizardContentSize;
  platformMediaType?: PlatformMediaType;
}

export function SocialMediaPostPublishParams({
  account,
  size = SocialMediaWizardContentSize.XLARGE,
  platformMediaType = PlatformMediaType.POST,
}: SocialMediaPostPublishParamsProps): ReactElement<any> | null {
  const wizardMobileNavView = useWizardMobileNavView();
  const publishingAction = useSocialPostPublishingAction();
  const publishingParams = usePublishingParams();
  const postPublishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const reelPublishingParamsForAccount = useReelPublishingParamsForAccount(account.id);
  const storyPublishingParamsForAccount = useStoryPublishingParamsForAccount(account.id);
  const dispatch = useAppDispatch();
  const isVideoDesign = useIsPublishingVideoForAccount();
  const platformMediaTypeRef = useRef(platformMediaType);
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isWeb = size === SocialMediaWizardContentSize.XLARGE || size === SocialMediaWizardContentSize.LARGE || size === SocialMediaWizardContentSize.MEDIUM;
  const contentRef = useRef<HTMLDivElement>(null);
  const [titleErrorMessage, setTitleErrorMessage] = useState<string | null>(null);
  const loadingState: LoadingStates = usePublishingStatusForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const accountResizeStatus = useAccountResizeCompletedStatusForPosters(account, platformMediaTypeRef.current);
  const wizardStep = useWizardStep();
  const previewParentRef = useRef<HTMLDivElement>(null);
  const animatingElementRef = useRef<HTMLDivElement>(null);
  const designsToPublishTo = useDesignsToPublish();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const designsGraphicsMetaMap = useWizardDesignsGraphicMetaMap();
  const isAnyPosterResizingForForAnyMediaType = useIsAnyPosterResizingForAccount(account.id);
  const designsGraphicsMetaMapForAccount = useDesignsGraphicsMetaMapForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const isAnyPosterResizingForAccount = useIsAnyPosterResizingForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const hasAnyPosterResizeErroredOut = useHasAnyPosterResizeErroredForAccount(account.id, platformMediaTypeRef.current);
  const isAnyPosterLoading = useIsAnyPosterLoadingForAccount(account.id, platformMediaTypeRef.current);
  const doesAnyPosterNeedResize = useDoesAnyPosterRequireResizeForAccount(account.id, platformMediaTypeRef.current);
  const storyGuideline = useStoryGuidelineForAccount(account.id);
  const [isPlatformAnimationRunning, setIsPlatformAnimationRunning] = useState(false);

  const {isPreviewLoading, setIsPreviewLoading, triggerDefaultResizing, updateResizingStatus, updateGraphicUrlInGraphicsMetaForDesign} = usePublishParamsResizePipeline({
    account,
    platformMediaTypeRef,
    platformMediaType,
  });

  const {onClickCover, getCoverUrlForSingleGraphicPost, isCoverCta, getTextForCoverCta} = usePublishParamsCoverFlow({
    account,
    platformMediaType,
    platformMediaTypeRef,
    isVideoDesign,
  });

  const runIncomingAnimation = (): void => {
    if (previewParentRef?.current) {
      previewParentRef.current.classList.remove(styles.opacityFade);
      previewParentRef.current.classList.add(styles.fade);
    }
  };

  const runOutgoingAnimation = (): void => {
    setTimeout((): void => {
      if (previewParentRef?.current) {
        previewParentRef.current.classList.add(styles.fadeBack);
      }
    }, 500);
  };

  const onCaptionTransitionEnd = (event: TransitionEvent): void => {
    if (event.target === previewParentRef.current) {
      if (previewParentRef.current.classList.contains(styles.fadeBack)) {
        previewParentRef.current.classList.remove(styles.fade);
        previewParentRef.current.classList.remove(styles.fadeBack);
        previewParentRef.current.classList.add(styles.opacityFade);
      }
    }
  };

  const onSocialPostCaptionEvent = (): void => {
    if (previewParentRef?.current) {
      runIncomingAnimation();
      runOutgoingAnimation();
    }
  };

  const onPlatformSelectionTransitionEvent = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail as SocialPostPlatformSelectionTransitionEventDetail;

    const platformMediaTypeFromEvent = detail.platformSelection;
    const {accountId} = detail;

    if (account.id !== accountId) {
      return;
    }

    if (platformMediaTypeFromEvent === platformMediaTypeRef.current && animatingElementRef?.current) {
      animatingElementRef?.current?.classList.add(styles.fadeIn);
      setIsPlatformAnimationRunning(true);
    }
  };

  const onMediaTypeAnimationEnd = (): void => {
    if (!animatingElementRef?.current) {
      return;
    }

    animatingElementRef.current.classList.remove(styles.fadeIn);
    setIsPlatformAnimationRunning(false);
  };

  useEffect(() => {
    window.addEventListener(SocialPostCaptionOverwriteEvent, onSocialPostCaptionEvent);
    window.addEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);

    return (): void => {
      window.removeEventListener(SocialPostCaptionOverwriteEvent, onSocialPostCaptionEvent);
      window.removeEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);
    };
  }, [platformMediaType]);

  const setTitleError = (newValue: string | null): void => {
    setTitleErrorMessage(newValue);
  };

  useEffect(() => {
    if (isAnyPosterResizingForAccount) {
      setIsPreviewLoading(true);
    }

    if (hasAnyPosterResizeErroredOut) {
      setIsPreviewLoading(false);
    }
  }, [isAnyPosterResizingForAccount, hasAnyPosterResizeErroredOut]);

  const onReelToggle = (newReelToggleValue: boolean): void => {
    if (newReelToggleValue && storyPublishingParamsForAccount) {
      dispatch(setMediaTypeSelectionToReelAndStory(account.id));
      return;
    }

    if (newReelToggleValue) {
      dispatch(setMediaTypeSelectionToReel(account.id));
      return;
    }

    if (storyPublishingParamsForAccount) {
      dispatch(setMediaTypeSelectionToPostAndStory(account.id));
      return;
    }

    dispatch(setMediaTypeSelectionToPost(account.id));
  };

  const getFormElementsIfNeeded = (): ReactElement<any> | null => {
    if (platformMediaType === PlatformMediaType.STORIES && publishingAction === undefined) {
      return null;
    }

    if (publishingAction === undefined) {
      return getFormElementsByGraphicType();
    }

    return <PublishingPost platformMediaType={platformMediaTypeRef.current} publishingAction={publishingAction} account={account} />;
  };

  const isPublishingActionHidingMultiplePosts = (): boolean => {
    return publishingAction === SocialPostPublishingAction.SCHEDULE || publishingAction === SocialPostPublishingAction.SAVE;
  };

  const getClassForPublishingAction = (): string => {
    if (isPublishingActionHidingMultiplePosts() && publishingParams && accountIdsToPublishTo.length > 0) {
      const firstAccountId: number = accountIdsToPublishTo[0];

      if (account.id !== firstAccountId) {
        return '_invisible';
      }
    }

    return '';
  };

  const getFormElementsByGraphicType = (): ReactElement<any> | null => {
    return (
      <PlatformPublishFields
        account={account}
        isVideoDesign={isVideoDesign}
        titleErrorMessage={titleErrorMessage}
        setTitleError={setTitleError}
        isAnyPosterResizingForForAnyMediaType={isAnyPosterResizingForForAnyMediaType}
        isReelMediaType={platformMediaTypeRef.current === PlatformMediaType.REELS}
        onReelToggle={onReelToggle}
      />
    );
  };

  const getFieldSpaceIfRequired = (): ReactElement<any> | null => {
    if (isPublishingActionHidingMultiplePosts()) {
      return null;
    }

    return <FieldSpacer />;
  };

  const getPreviewOverlay = (): ReactElement<any> => {
    if (loadingState !== LoadingStates.LOADING || wizardStep !== SocialMediaWizardStep.PUBLISH) {
      return <></>;
    }

    return <div className={styles.loadingOverlay} />;
  };

  const onLoadCallback = (): void => {
    setIsPreviewLoading(false);
  };

  const getSelectionIndexForRadioButton = (): string => {
    const isReelOrPost = postPublishingParamsForAccount !== null || reelPublishingParamsForAccount !== null;

    if (storyPublishingParamsForAccount && isReelOrPost) {
      return SocialMediaWizardMediaTypeRadioButtonIds.BOTH;
    }

    if (storyPublishingParamsForAccount) {
      return SocialMediaWizardMediaTypeRadioButtonIds.STORY;
    }

    return SocialMediaWizardMediaTypeRadioButtonIds.POST;
  };

  const onSelectionChange = (selectedId: SocialMediaWizardMediaTypeRadioButtonIds): void => {
    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.POST) {
      dispatch(setMediaTypeSelectionToPost(account.id));
    }

    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.STORY) {
      dispatch(setMediaTypeSelectionToStory(account.id));
    }

    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.BOTH) {
      if (reelPublishingParamsForAccount) {
        dispatch(setMediaTypeSelectionToReelAndStory(account.id));
        return;
      }

      if (postPublishingParamsForAccount || storyPublishingParamsForAccount) {
        dispatch(setMediaTypeSelectionToPostAndStory(account.id));
      }
    }
  };

  const shouldShowPlatformMediaTypeRadioButtons = (): boolean => {
    if (publishingAction !== undefined) {
      return false;
    }

    const isMultiPosting = designsToPublishTo.length > 1;

    if (isMultiPosting || (!isFacebookAccount(account) && !isInstagramAccount(account))) {
      return false;
    }

    if (isFacebookAccount(account) && isVideoDesign) {
      return false;
    }

    return true;
  };

  const getPlatformMediaTypeRadioButtons = (): ReactElement<any> | null => {
    const radioButtonOptions: RadioButtonOption[] = [
      {
        text: window.i18next.t('pmwjs_post'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.POST,
      },
      {
        text: window.i18next.t('pmwjs_story'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.STORY,
      },
      {
        text: window.i18next.t('pmwjs_both'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.BOTH,
      },
    ];

    if (!shouldShowPlatformMediaTypeRadioButtons()) {
      return null;
    }

    return (
      <RadioButton
        isDisabled={isAnyPosterResizingForForAnyMediaType}
        disabledTooltipText={isAnyPosterResizingForForAnyMediaType ? window.i18next.t('pmwjs_your_design_is_being_resized') : undefined}
        containerClasses={getRadioButtonContainerClasses()}
        selectionId={getSelectionIndexForRadioButton()}
        options={radioButtonOptions}
        onSelectionCallback={(selectedId: string) => {
          onSelectionChange(selectedId as SocialMediaWizardMediaTypeRadioButtonIds);
        }}
      />
    );
  };

  const getRadioButtonContainerClasses = (): string => {
    return `${styles.transitionClasses} ${isPlatformAnimationRunning ? styles.fadeIn : ''}`;
  };

  const getStoryGuidelineIfNeeded = (): ReactElement<any> | null => {
    if (publishingAction !== undefined) {
      return null;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      return <SocialMediaPublishParamsStoryGuideline storyGuideline={storyGuideline} />;
    }

    return null;
  };

  const onRemoveAccountClick = async (): Promise<void> => {
    const hideRemoveConfirmationPrompt = await getHideSMRemoveAccountConfirmationPreference();

    if (!hideRemoveConfirmationPrompt) {
      openRemoveAccountConfirmationPrompt();
      return;
    }

    doRemoveAccountFromPost();
  };

  const doRemoveAccountFromPost = (): void => {
    const postHasSingleAccount = accountIdsToPublishTo.length === 1;

    if (postHasSingleAccount) {
      dispatch(updatePublishingParams({}));
      dispatch(updateWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS));
      return;
    }

    dispatch(removeAccountIdFromPublishing(account.id));
    dispatch(removeAccountFromPublishingParams(account.id));
  };

  const onRemoveAccountPromptPermanentClosureConfirmation = (): void => {
    void doHideRemoveAccountConfirmationForUser();
  };

  const openRemoveAccountConfirmationPrompt = (): void => {
    openConfirmDeleteModal({
      title: window.i18next.t('pmwjs_remove_account'),
      text: window.i18next.t('pmwjs_removing_account_description'),
      ctaText: window.i18next.t('pmwjs_remove'),
      onDeleteConfirmation: doRemoveAccountFromPost,
      showPermanentClosureCheckbox: true,
      onPermanentClosureCheckboxConfirmation: onRemoveAccountPromptPermanentClosureConfirmation,
    });
  };

  const getWebContent = (): ReactElement<any> => {
    return (
      <div key={account.id} className="flex-h-row _full-width">
        <div className={`flex-v-row flex-content-start spacing-m-r-10 ${styles.contentContainer} ${getClassForPublishingAction()}`}>
          <SocialAccountLabel
            platformMediaType={platformMediaTypeRef.current}
            account={account}
            showAccountDetails
            className={isPublishingActionHidingMultiplePosts() ? '_hidden' : ''}
            onRemoveAccountClick={(): void => {
              void onRemoveAccountClick();
            }}
          />
          {getFieldSpaceIfRequired()}
          {getPlatformMediaTypeRadioButtons()}
          {getFieldSpaceIfRequired()}
          {getFormElementsIfNeeded()}
          {getStoryGuidelineIfNeeded()}
        </div>
        <div ref={animatingElementRef} onAnimationEnd={onMediaTypeAnimationEnd} className={styles.previewContent}>
          {getPreviewOverlay()}
          <div
            onTransitionEnd={onCaptionTransitionEnd}
            ref={previewParentRef}
            className={`${styles.previewWrapper} ${!isPlatformAnimationRunning ? styles.previewWrapperColor : ''} ${isPreviewLoading ? '' : styles.loaded}`}
            key={getCoverUrlForSingleGraphicPost}
          >
            <SocialPostContent
              designsToPublish={designsToPublishTo}
              onLoadCallback={onLoadCallback}
              areDetailsLoading={false}
              parentContainerRef={previewParentRef}
              type={hasAnyPosterResizeErroredOut ? SocialPostContentType.MEDIA_RESIZING_FAILURE : SocialPostContentType.ACCOUNT_PREVIEW}
              account={account}
              accountsToPublishTo={[]}
              publishingParams={publishingParams}
              designsGraphicMetaMap={designsGraphicsMetaMapForAccount}
              platformMediaType={platformMediaTypeRef.current}
            />
            <div className={`spacing-m-b-3 spacing-m-t-15 ${styles.ctaWrapper}`}>
              <PublishParamsPreviewCta
                wizardStep={wizardStep}
                accountResizeStatus={accountResizeStatus}
                doesAnyPosterNeedResize={doesAnyPosterNeedResize}
                publishingAction={publishingAction}
                accountType={account.type}
                isAnyPosterLoading={isAnyPosterLoading}
                isPreviewLoading={isPreviewLoading}
                isCoverCta={isCoverCta()}
                coverCtaText={getTextForCoverCta()}
                onResize={onClickResizeDesign}
                onRetry={triggerDefaultResizing}
                onClickCover={onClickCover}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleResizeCompleted = (graphicURL: string, design: DesignSelection): void => {
    if (!design) {
      console.error('No poster was selected for user triggered resizing');
      return;
    }

    const {posterHashedId, pageHashedId} = design;
    const {isVideo} = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId];

    dispatch(
      updatePublishableUrl({
        accountId: account.id,
        posterHashId: posterHashedId,
        pageHashId: pageHashedId,
        platformMediaType: platformMediaTypeRef.current,
        graphicUrl: graphicURL,
      } as PublishableUrlUpdatePayload)
    );

    updateGraphicUrlInGraphicsMetaForDesign(graphicURL, design, isVideo);
    updateResizingStatus(AccountResizeStatus.RESIZE_DONE, design, platformMediaTypeRef.current);
    setIsPreviewLoading(false);
  };

  const handleResizeError = (designThatHasErrored: DesignSelection): void => {
    const {posterHashedId, pageHashedId} = designThatHasErrored;
    const originalPreviewUrl = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].graphicURL;
    const {isVideo} = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId];

    updateResizingStatus(AccountResizeStatus.RESIZE_ERROR, designThatHasErrored, platformMediaTypeRef.current);
    updateGraphicUrlInGraphicsMetaForDesign(originalPreviewUrl, designThatHasErrored, isVideo);
  };

  const handleCancelResize = noop;

  const handleDoneClicked = (design: DesignSelection): void => {
    if (!design) {
      console.error('No poster was selected for resize');
      return;
    }

    setIsPreviewLoading(true);
    const {isVideo} = designsGraphicsMetaMapForAccount[design.posterHashedId][design.pageHashedId];
    updateGraphicUrlInGraphicsMetaForDesign('', design, isVideo);
    updateResizingStatus(AccountResizeStatus.RESIZING, design, platformMediaTypeRef.current);
  };

  const onResizeDesignSelectionCallback = (design: DesignSelection): void => {
    dispatch(updateDesignToResize(design));
    dispatch(updateMultiGraphicDesignSelectorMode(MultiGraphicDesignSelectorModes.RESIZE));
  };

  const setDesignActionPanelModeToSelect = (): void => {
    dispatch(updateMultiGraphicDesignSelectorMode(MultiGraphicDesignSelectorModes.SELECT));
  };

  const onClickResizeDesign = (): void => {
    if (designsToPublishTo.length === 1) {
      onResizeDesignSelectionCallback(designsToPublishTo[0]);
    } else {
      setDesignActionPanelModeToSelect();
    }

    const designActionModalProps: SocialPostsDesignActionModalProps = {
      account,
      designsToPublish: designsToPublishTo,
      designsGraphicsMetaMap,
      designActionCallback: onResizeDesignSelectionCallback,
      actionPurpose: MultiPostDesignActionPurposes.RESIZE,
      resizePanelOptions: {
        account,
        panelId: SocialPostSelectDesignPanelID,
        onCancelResize: handleCancelResize,
        onResizeCompleted: handleResizeCompleted,
        onResizeError: handleResizeError,
        onDoneClicked: handleDoneClicked,
        platformMediaType: platformMediaTypeRef.current,
      },
    };

    openDesignActionModal(designActionModalProps);
  };

  const getClassesForPreviewWrapper = (): string => {
    let previewWrapperClass = styles.previewWrapper;

    if (!isPreviewLoading) {
      previewWrapperClass += ` ${styles.loaded}`;
    }

    return previewWrapperClass;
  };

  const getMobilePreviewContent = (): ReactElement<any> => {
    return (
      <div key={`content-post-${account.id}`} className={`_full-width flex-column-justify-center flex-items-center ${styles.mobilePreviewWrapper}`}>
        <div onTransitionEnd={onCaptionTransitionEnd} ref={previewParentRef} className={getClassesForPreviewWrapper()} key={getCoverUrlForSingleGraphicPost}>
          <SocialPostContent
            designsToPublish={designsToPublishTo}
            onLoadCallback={onLoadCallback}
            areDetailsLoading={false}
            parentContainerRef={previewParentRef}
            type={SocialPostContentType.ACCOUNT_PREVIEW}
            account={account}
            accountsToPublishTo={[]}
            publishingParams={publishingParams}
            designsGraphicMetaMap={designsGraphicsMetaMapForAccount}
            platformMediaType={platformMediaTypeRef.current}
          />
        </div>
        <div className={`spacing-m-b-3 spacing-m-t-15 ${styles.ctaWrapper}`}>
          <PublishParamsPreviewCta
            wizardStep={wizardStep}
            accountResizeStatus={accountResizeStatus}
            doesAnyPosterNeedResize={doesAnyPosterNeedResize}
            publishingAction={publishingAction}
            accountType={account.type}
            isAnyPosterLoading={isAnyPosterLoading}
            isPreviewLoading={isPreviewLoading}
            isCoverCta={isCoverCta()}
            coverCtaText={getTextForCoverCta()}
            onResize={onClickResizeDesign}
            onRetry={triggerDefaultResizing}
            onClickCover={onClickCover}
          />
        </div>
      </div>
    );
  };

  const getMobilePreview = (): ReactElement<any> => {
    return getMobilePreviewContent();
  };

  const handleRemoveAccountClick = (): void => {
    void onRemoveAccountClick();
  };

  const getMobileContent = (): ReactElement<any> => {
    if (wizardMobileNavView === WizardMobileNavView.CONTENT) {
      return (
        <div ref={contentRef} className="flex-h-row _full-width">
          <div className={`flex-v-row _full-width ${isSmallScreen || isExtraSmallScreen ? '' : 'spacing-m-r-10'}`}>
            <SocialAccountLabel account={account} showAccountDetails onRemoveAccountClick={handleRemoveAccountClick} />
            <FieldSpacer />
            {getPlatformMediaTypeRadioButtons()}
            {shouldShowPlatformMediaTypeRadioButtons() ? <FieldSpacer /> : null}
            {getFormElementsIfNeeded()}
            {getStoryGuidelineIfNeeded()}
          </div>
        </div>
      );
    }

    return getMobilePreview();
  };

  return isWeb ? getWebContent() : getMobileContent();
}
